import React, { useEffect, useState, useCallback } from 'react';
import { notificationsApi } from '../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called whenever the unread count changes so the parent (TopBar) can update the dot. */
  onUnreadCountChange?: (count: number) => void;
}

interface NotificationRow {
  id: string;
  title: string;
  body: string;
  type: string;
  unread: boolean;
  createdAt: string;
}

/**
 * Bildirimler popup — real DB data only.
 *
 * Hits GET /notifications for the current user (role-agnostic — the backend
 * already filters by recipient_user_id). Works for student / teacher / parent.
 */

const iconForType = (type: string): string => {
  if (type?.includes('coach')) return '/a-digikoc-mascot.png'; // B11 Digikoç nudges
  if (type?.includes('appointment')) return '/a-bell.png';
  if (type?.includes('assignment')) return '/a-odevicon.png';
  if (type?.includes('exam')) return '/a-odevicon.png';
  return '/a-bell.png';
};

const mapRow = (r: any): NotificationRow => ({
  id: String(r.notification_id ?? r.notifications?.id ?? r.id ?? ''),
  title: r.notifications?.title ?? r.title ?? 'Bildirim',
  body: r.notifications?.body ?? r.body ?? '',
  type: r.notifications?.type ?? r.type ?? 'genel',
  unread: !(r.is_read ?? false),
  createdAt: r.notifications?.createdAt ?? r.createdAt ?? '',
});

const NotificationsPopup: React.FC<Props> = ({ open, onClose, onUnreadCountChange }) => {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    notificationsApi.list()
      .then((res: any) => {
        const mapped = (res.data ?? []).map(mapRow);
        setRows(mapped);
        onUnreadCountChange?.(mapped.filter((r: NotificationRow) => r.unread).length);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [onUnreadCountChange]);

  // Load on open, then B20: while the popup stays open, re-poll every 2 minutes
  // so the list picks up notifications that arrive meanwhile. Pauses when the
  // tab is hidden. A re-fetch here also reconciles the optimistic mark-all-read
  // (the server has persisted the read state, so rows come back read).
  useEffect(() => {
    if (!open) return;
    load();
    const POLL_MS = 120_000;
    const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [open, load]);

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setRows(prev => prev.map(r => ({ ...r, unread: false })));
      onUnreadCountChange?.(0);
    } catch { /* silent */ }
  };

  // Clicking a single notification marks just that one read (optimistic).
  const markOne = async (id: string) => {
    const target = rows.find(r => r.id === id);
    if (!target || !target.unread) return; // already read → no-op
    setRows(prev => {
      const next = prev.map(r => (r.id === id ? { ...r, unread: false } : r));
      onUnreadCountChange?.(next.filter(r => r.unread).length);
      return next;
    });
    try { await notificationsApi.markRead(id); } catch { /* silent — optimistic */ }
  };

  if (!open) return null;
  const unreadCount = rows.filter(n => n.unread).length;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-[65px] xl:top-[80px] right-[80px] xl:right-[220px] z-50 w-[320px] xl:w-[420px] bg-white rounded-[20px] shadow-xl border border-gray-100 p-4 xl:p-5">
        <div className="flex items-center justify-between mb-3 xl:mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-[16px] xl:text-[20px] font-semibold text-[#2b2b2b]">Bildirimler</h3>
            {unreadCount > 0 && (
              <span className="bg-[#e5599b] text-white text-[11px] font-bold w-[20px] h-[20px] rounded-full flex items-center justify-center">{unreadCount}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-[11px] font-semibold text-[#3973bf] hover:underline">Tümünü okundu işaretle</button>
            )}
            <button onClick={onClose} aria-label="Kapat">
              <img src="/a-bell.png" alt="" className="w-[20px] h-[24px] xl:w-[24px] xl:h-[28px] object-contain" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-400 text-[13px] py-8">Bildiriminiz yok.</p>
        ) : (
          <div className="space-y-2.5 max-h-[400px] overflow-y-auto">
            {rows.map(n => (
              <div key={n.id}
                onClick={() => markOne(n.id)}
                role="button"
                className={`rounded-[14px] p-3 xl:p-4 flex items-start gap-3 transition-colors cursor-pointer ${
                  n.unread ? 'border-2 border-[#3973bf] bg-blue-50 hover:bg-blue-100' : 'border border-gray-200 bg-white hover:bg-gray-50'
                }`}>
                <img src={iconForType(n.type)} alt="" className="w-[36px] h-[36px] xl:w-[44px] xl:h-[44px] rounded-full object-contain flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] xl:text-[16px] font-semibold text-[#2b2b2b]">{n.title}</p>
                  <p className="text-[11px] xl:text-[13px] text-gray-500 mt-0.5 line-clamp-3">{n.body}</p>
                  {n.createdAt && (
                    <p className="text-[10px] xl:text-[11px] text-gray-400 mt-1">
                      {new Date(n.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
                {n.unread && <div className="w-2.5 h-2.5 rounded-full bg-[#3973bf] flex-shrink-0 mt-1.5" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationsPopup;
